import { useState } from 'react';
import type { ReactNode } from 'react';
import { useActions, useLive, useRuntime } from './context';
import { useRemote } from './useRemote';
import { formatClock, weekdayNames } from '../mirror/time';
import { TeamSeatList } from '../wire';
import type { Blackout, Resource, Service, TeamSeat } from '../wire';
import { serviceRoleSides } from './calendar-grid';
import type { DayHours } from '@yourstore/domain';
import { useLocale, useT } from '../messages';
import type { T } from '../messages';

/**
 * **The team** (`docs/plan-2026-09-21.md` S2) — the people and the rooms this
 * business schedules against, their weeks, their time off, and who does what.
 *
 * ## The solo owner never sees the chrome
 *
 * The first owner of this product works alone in one room
 * (`docs/lovespa-discovery.md` §1). Everything on this screen is built for the
 * business she has said she may end up running — *"with employees and digital
 * calendars"* — and none of it may cost her anything today. So the screen has
 * two shapes and picks between them by **counting rows**, not by a setting:
 *
 *  - **one resource** (or none): a single quiet card. No people section, no
 *    rooms section, no service × person matrix, no lane picker. One sentence
 *    saying the day is hers, and one button to add somebody the day she hires.
 *  - **more than one**: the full screen, which is the only place any of this
 *    vocabulary appears anywhere in the app.
 *
 * The matrix has its own threshold for the same reason. A grid of one column is
 * a grid that tells the owner nothing and asks her to learn a widget, so it is
 * drawn only when there are at least two people (or two rooms) for a service to
 * be assigned *between*.
 *
 * ## What is read where
 *
 * People, rooms, services and time off come from the **mirror** (§8.1) and the
 * writes go through the **outbox** (§8.2), exactly as the Settings screen's
 * hours edits do: an owner in a tunnel can add Thursday to somebody's week and
 * it lands when the radio does. The one online read is the **seat roster** —
 * who has a login — which is `useRemote`'s rule rather than `useLive`'s and says
 * why at `TeamSeat` in `wire.ts`.
 *
 * ## Conflicts are listed, never resolved
 *
 * Time off that lands on top of a booking does not cancel it and there is no
 * control here that does. The closure is written, the bookings inside it are
 * named with the guest and the treatment, and the owner decides — which is
 * spec §8.3's rule about the whole offline design applied to the one place a
 * *deliberate* collision is routine.
 */

const ROOM_KINDS = new Set(['room', 'equipment', 'table_pool']);

export function Team(): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const session = useLive(() => runtime.mirror.session(), null);
  const resources = useLive(() => runtime.mirror.resources(), []);
  const services = useLive(() => runtime.mirror.services(), []);
  const blackouts = useLive(() => runtime.mirror.blackouts(), []);

  /**
   * The owner line, drawn the way the Concierge panel draws its: `core-api`
   * answers `403` to a `staff` or `readonly` seat on `resources.create` and
   * `resources.update` (`routes/registry.ts`, `owner: true`), so a screen that
   * offered those buttons would be offering a door that does not open. Time off
   * is deliberately **not** owner-only — see the same file — so its controls are
   * drawn for any seat that may write.
   */
  const isOwner = session?.user.role === 'owner';
  const mayWrite = session?.can_write === true;
  const timezone = session?.tenant.timezone ?? 'UTC';

  const seats = useRemote(
    (signal) =>
      runtime.api.request({ method: 'GET', path: '/team/seats', schema: TeamSeatList, signal }),
    [],
    { pollMs: 60_000, enabled: isOwner },
  );

  const people = resources.filter((r) => r.kind === 'person');
  const rooms = resources.filter((r) => ROOM_KINDS.has(r.kind));
  /**
   * "It is just her." Counted from the rows rather than from a flag, and
   * **including retired ones**, because a tenant that once had two people and
   * retired one has still learned what this screen is and should not have it
   * fold up underneath her.
   */
  const solo = resources.length <= 1;

  return (
    <section className="view view--settings">
      <div className="view__header">
        <h1>{t('team.title')}</h1>
      </div>

      {solo ? (
        <SoloCard owner={isOwner} only={resources[0]} />
      ) : (
        <>
          <PeoplePanel
            people={people}
            seats={seats.data?.seats ?? []}
            owner={isOwner}
            mayWrite={mayWrite}
            timezone={timezone}
            blackouts={blackouts}
          />
          <RoomsPanel rooms={rooms} owner={isOwner} />
          <MatrixPanel people={people} rooms={rooms} services={services} owner={isOwner} />
        </>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* The quiet card                                                      */
/* ------------------------------------------------------------------ */

/**
 * One sentence and one button.
 *
 * Deliberately not a list of one. A solo owner's answer to "who works here" is
 * *me*, and showing her a row with a name she never chose — the importer calls
 * it "Treatment Room" — beside an empty matrix would be teaching her a model to
 * explain a screen she did not need.
 */
function SoloCard({ owner, only }: { owner: boolean; only: Resource | undefined }): ReactNode {
  const t = useT();
  return (
    <article className="panel">
      <h2>{t('team.justYouTitle')}</h2>
      <p className="muted">{t('team.justYouBody')}</p>
      {owner ? <AddPersonRow /> : null}
      {only === undefined ? null : (
        <p className="muted">{t('team.soloLane', { name: only.name })}</p>
      )}
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* People                                                              */
/* ------------------------------------------------------------------ */

function PeoplePanel({
  people,
  seats,
  owner,
  mayWrite,
  timezone,
  blackouts,
}: {
  people: Resource[];
  seats: TeamSeat[];
  owner: boolean;
  mayWrite: boolean;
  timezone: string;
  blackouts: Blackout[];
}): ReactNode {
  const t = useT();
  const [editing, setEditing] = useState<string | null>(null);
  const actions = useActions();

  return (
    <article className="panel">
      <h2>{t('team.people')}</h2>
      <ul className="service-list">
        {people.map((person) => (
          <li key={person.resource_id} className={person.active ? '' : 'is-retired'}>
            <div>
              <strong>{person.name}</strong>
              <small>
                <WeekSummary hours={person.hours} />
                <SeatLine person={person} seats={seats} />
              </small>
              {editing === person.resource_id ? (
                <PersonEditor
                  person={person}
                  seats={seats}
                  owner={owner}
                  mayWrite={mayWrite}
                  timezone={timezone}
                  blackouts={blackouts.filter((b) => b.resource_id === person.resource_id)}
                  onDone={() => setEditing(null)}
                />
              ) : null}
            </div>
            <div className="row-actions">
              <button
                type="button"
                className="link-button"
                onClick={() =>
                  setEditing(editing === person.resource_id ? null : person.resource_id)
                }
              >
                {editing === person.resource_id ? t('common.close') : t('common.edit')}
              </button>
              {owner ? (
                <button
                  type="button"
                  className="link-button"
                  onClick={() =>
                    void actions.updateResource({
                      resourceId: person.resource_id,
                      name: person.name,
                      changes: { active: !person.active },
                    })
                  }
                >
                  {/*
                    Retire, never delete. There is no `DELETE /v1/resources` and
                    there will not be: every appointment somebody delivered
                    points at this row, `appointment_resources.resource_id` is
                    ON DELETE RESTRICT, and "who did Dana see last March" is a
                    question somebody asks.
                  */}
                  {person.active ? t('common.retire') : t('common.restore')}
                </button>
              ) : null}
            </div>
          </li>
        ))}
        {people.length === 0 ? <li className="muted">{t('team.nobodyYet')}</li> : null}
      </ul>
      {owner ? <AddPersonRow /> : null}
    </article>
  );
}

function SeatLine({ person, seats }: { person: Resource; seats: TeamSeat[] }): ReactNode {
  const t = useT();
  if (person.staff_user_id === null) return null;
  const seat = seats.find((s) => s.staff_user_id === person.staff_user_id);
  // The roster is an online read, so a device in a tunnel knows *that* she has a
  // login and not which address it is. Saying the first honestly beats saying
  // nothing, and beats guessing the second.
  return <>{t('team.signsInAs', { email: seat?.email ?? t('team.someLogin') })}</>;
}

function AddPersonRow(): ReactNode {
  const actions = useActions();
  const t = useT();
  const [name, setName] = useState('');

  return (
    <div className="editor editor--inline">
      <input
        placeholder={t('common.name')}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <button
        type="button"
        className="button"
        disabled={name.trim() === ''}
        onClick={() => {
          void actions.createResource({ name: name.trim(), kind: 'person' });
          setName('');
        }}
      >
        {t('team.addPerson')}
      </button>
    </div>
  );
}

/**
 * One person's own facts: her name, her place in the list, her login, her week
 * and her time off.
 *
 * Only what changed is sent. `UpdateResourceBody` is a partial and the server
 * treats `undefined` as "leave it alone", and that matters more here than
 * anywhere else in the app because `staff_user_id` has **three** meanings — a
 * string links, `null` unlinks, and absent must stay absent. A form that always
 * posted every field would unlink somebody every time the owner fixed a typo.
 */
function PersonEditor({
  person,
  seats,
  owner,
  mayWrite,
  timezone,
  blackouts,
  onDone,
}: {
  person: Resource;
  seats: TeamSeat[];
  owner: boolean;
  mayWrite: boolean;
  timezone: string;
  blackouts: Blackout[];
  onDone: () => void;
}): ReactNode {
  const actions = useActions();
  const t = useT();
  const [name, setName] = useState(person.name);
  const [sortOrder, setSortOrder] = useState(String(person.sort_order));
  const [seatId, setSeatId] = useState(person.staff_user_id ?? '');
  const [week, setWeek] = useState<DayHours[] | null>(null);

  // `null` on the row means "follows the business's hours". Opening the week
  // editor therefore has to start from *something*, and it starts from the
  // house's shape rather than from an empty week, so the first thing an owner
  // does is take a day away rather than build one from nothing.
  const ownWeek = week ?? person.hours;

  const save = (): void => {
    const changes: Record<string, unknown> = {};
    if (name !== person.name) changes['name'] = name;
    if (Number(sortOrder) !== person.sort_order) changes['sort_order'] = Number(sortOrder);
    if (owner && seatId !== (person.staff_user_id ?? '')) {
      changes['staff_user_id'] = seatId === '' ? null : seatId;
    }
    if (week !== null) changes['hours'] = week;
    if (Object.keys(changes).length > 0) {
      void actions.updateResource({
        resourceId: person.resource_id,
        name: person.name,
        changes,
      });
    }
    onDone();
  };

  return (
    <div className="team-editor">
      {owner ? (
        <div className="editor editor--inline">
          <input value={name} onChange={(e) => setName(e.target.value)} />
          <label className="field--row">
            <span>{t('team.order')}</span>
            <input
              inputMode="numeric"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
          </label>
          <label className="field--row">
            <span>{t('team.login')}</span>
            <select value={seatId} onChange={(e) => setSeatId(e.target.value)}>
              <option value="">{t('team.noLogin')}</option>
              {seats.map((seat) => (
                <option key={seat.staff_user_id} value={seat.staff_user_id}>
                  {seat.display_name === '' ? seat.email : `${seat.display_name} · ${seat.email}`}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {owner ? (
        <WeekEditor
          week={ownWeek}
          onChange={setWeek}
          onFollowHouse={() => setWeek(null)}
          following={person.hours === null && week === null}
        />
      ) : null}

      <TimeOffPanel person={person} mayWrite={mayWrite} timezone={timezone} blackouts={blackouts} />

      {owner ? (
        <button type="button" className="button button--primary" onClick={save}>
          {t('common.save')}
        </button>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Weeks                                                               */
/* ------------------------------------------------------------------ */

/**
 * "Follows the business's hours", or the week she actually works.
 *
 * `hours` of `null` on the row is not a missing value — it is the statement
 * that this person has no override, which is what `loadResources` in
 * `packages/store` resolves to the tenant's own week. So "follow the house" is a
 * real button that sends `hours: null`, and not a checkbox that writes a copy of
 * the house's week which would then stop tracking it.
 *
 * The editor keeps `windows` as a **list** for `Settings.tsx`' stated reason:
 * the domain type is a list because a day can be split, and an editor that only
 * ever wrote one window would silently discard somebody's evening.
 */
function WeekEditor({
  week,
  onChange,
  onFollowHouse,
  following,
}: {
  week: DayHours[] | null;
  onChange: (week: DayHours[]) => void;
  onFollowHouse: () => void;
  following: boolean;
}): ReactNode {
  const t = useT();
  const locale = useLocale();
  const dayNames = weekdayNames(locale);
  const days = week ?? defaultWeek();

  const change = (weekday: number, apply: (day: DayHours) => DayHours): void => {
    onChange(days.map((d) => (d.weekday === weekday ? apply(d) : d)));
  };

  return (
    <div className="team-week">
      <p className="muted">{following ? t('team.followsHouse') : t('team.ownWeek')}</p>
      {following ? (
        <button type="button" className="link-button" onClick={() => onChange(defaultWeek())}>
          {t('team.giveOwnWeek')}
        </button>
      ) : (
        <>
          <ul className="hours-list">
            {days.map((day) => (
              <li key={day.weekday}>
                <span className="hours-list__name">{dayNames[day.weekday]}</span>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={day.closed}
                    onChange={(e) =>
                      change(day.weekday, (d) => ({ ...d, closed: e.target.checked }))
                    }
                  />
                  <span>{t('team.notWorking')}</span>
                </label>
                {day.closed
                  ? null
                  : day.windows.map((w, index) => (
                      <span key={index} className="hours-list__window">
                        <input
                          type="time"
                          value={w.start}
                          onChange={(e) =>
                            change(day.weekday, (d) => ({
                              ...d,
                              windows: d.windows.map((x, i) =>
                                i === index ? { ...x, start: e.target.value } : x,
                              ),
                            }))
                          }
                        />
                        <input
                          type="time"
                          value={w.end}
                          onChange={(e) =>
                            change(day.weekday, (d) => ({
                              ...d,
                              windows: d.windows.map((x, i) =>
                                i === index ? { ...x, end: e.target.value } : x,
                              ),
                            }))
                          }
                        />
                        <button
                          type="button"
                          className="link-button link-button--danger"
                          onClick={() =>
                            change(day.weekday, (d) => ({
                              ...d,
                              windows: d.windows.filter((_, i) => i !== index),
                            }))
                          }
                        >
                          ×
                        </button>
                      </span>
                    ))}
                {day.closed ? null : (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() =>
                      change(day.weekday, (d) => ({
                        ...d,
                        windows: [...d.windows, { start: '09:00', end: '17:00' }],
                      }))
                    }
                  >
                    {t('settings.addWindow')}
                  </button>
                )}
              </li>
            ))}
          </ul>
          <button type="button" className="link-button" onClick={onFollowHouse}>
            {t('team.backToHouse')}
          </button>
        </>
      )}
    </div>
  );
}

function WeekSummary({ hours }: { hours: DayHours[] | null }): ReactNode {
  const t = useT();
  const locale = useLocale();
  if (hours === null) return <>{t('team.followsHouse')}</>;
  const names = weekdayNames(locale);
  const working = hours.filter((d) => !d.closed && d.windows.length > 0);
  if (working.length === 0) return <>{t('team.worksNoDays')}</>;
  return <>{working.map((d) => names[d.weekday]).join(', ')}</>;
}

function defaultWeek(): DayHours[] {
  return Array.from({ length: 7 }, (_, weekday) => ({
    weekday,
    closed: false,
    windows: [{ start: '09:00', end: '17:00' }],
  }));
}

/* ------------------------------------------------------------------ */
/* Time off                                                            */
/* ------------------------------------------------------------------ */

function TimeOffPanel({
  person,
  mayWrite,
  timezone,
  blackouts,
}: {
  person: Resource;
  mayWrite: boolean;
  timezone: string;
  blackouts: Blackout[];
}): ReactNode {
  const actions = useActions();
  const t = useT();
  const locale = useLocale();
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');

  return (
    <div className="team-timeoff">
      <h3>{t('team.timeOff')}</h3>
      <ul className="blackout-list">
        {blackouts.map((b) => (
          <li key={b.blackout_id}>
            <div>
              <span>
                {t('settings.blackoutSpan', {
                  from: formatClock(b.start, timezone, locale),
                  to: formatClock(b.end, timezone, locale),
                })}
                {b.reason === null || b.reason === ''
                  ? ''
                  : t('settings.blackoutReason', { reason: b.reason })}
              </span>
              {(b.conflicts ?? []).length > 0 ? (
                <ConflictList blackout={b} timezone={timezone} />
              ) : null}
            </div>
            {mayWrite ? (
              <button
                type="button"
                className="link-button link-button--danger"
                onClick={() =>
                  void actions.deleteBlackout({ blackoutId: b.blackout_id, reason: b.reason })
                }
              >
                {t('common.remove')}
              </button>
            ) : null}
          </li>
        ))}
        {blackouts.length === 0 ? <li className="muted">{t('team.noTimeOff')}</li> : null}
      </ul>
      {mayWrite ? (
        <div className="editor editor--inline">
          <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
          <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
          <input
            placeholder={t('settings.reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <button
            type="button"
            className="button"
            disabled={start === '' || end === ''}
            onClick={() => {
              void actions.createTimeOff({
                resourceId: person.resource_id,
                personName: person.name,
                startIso: new Date(start).toISOString(),
                endIso: new Date(end).toISOString(),
                ...(reason.trim() === '' ? {} : { reason: reason.trim() }),
              });
              setStart('');
              setEnd('');
              setReason('');
            }}
          >
            {t('team.bookTimeOff')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The telephone calls.
 *
 * Named, with the guest and the treatment, and with **no button beside them**.
 * The two ways out of a collision are to move the appointment (the calendar) or
 * to shorten the time off (the control directly above), and both are decisions.
 * A "cancel these" button here would be the merge spec §8.3 refuses, wearing a
 * different hat.
 */
function ConflictList({ blackout, timezone }: { blackout: Blackout; timezone: string }): ReactNode {
  const t = useT();
  const locale = useLocale();
  return (
    <>
      <p className="warning">
        {t('team.conflictsHeading', { count: (blackout.conflicts ?? []).length })}
      </p>
      <ul className="task__conflicts">
        {(blackout.conflicts ?? []).map((c) => (
          <li key={c.appointment_id}>
            {t('team.conflictLine', {
              when: formatClock(c.start, timezone, locale),
              who: c.client_name,
              what: c.service_name,
            })}
          </li>
        ))}
      </ul>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Rooms                                                               */
/* ------------------------------------------------------------------ */

function RoomsPanel({ rooms, owner }: { rooms: Resource[]; owner: boolean }): ReactNode {
  const actions = useActions();
  const t = useT();
  const [name, setName] = useState('');

  return (
    <article className="panel">
      <h2>{t('team.rooms')}</h2>
      <ul className="service-list">
        {rooms.map((room) => (
          <li key={room.resource_id} className={room.active ? '' : 'is-retired'}>
            <div>
              <strong>{room.name}</strong>
              <small>
                {room.units > 1 ? (
                  t('team.units', { units: room.units })
                ) : (
                  <WeekSummary hours={room.hours} />
                )}
              </small>
            </div>
            {owner ? (
              <div className="row-actions">
                <button
                  type="button"
                  className="link-button"
                  onClick={() =>
                    void actions.updateResource({
                      resourceId: room.resource_id,
                      name: room.name,
                      changes: { active: !room.active },
                    })
                  }
                >
                  {room.active ? t('common.retire') : t('common.restore')}
                </button>
              </div>
            ) : null}
          </li>
        ))}
        {rooms.length === 0 ? <li className="muted">{t('team.noRooms')}</li> : null}
      </ul>
      {owner ? (
        <div className="editor editor--inline">
          <input
            placeholder={t('common.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button
            type="button"
            className="button"
            disabled={name.trim() === ''}
            onClick={() => {
              void actions.createResource({ name: name.trim(), kind: 'room' });
              setName('');
            }}
          >
            {t('team.addRoom')}
          </button>
        </div>
      ) : null}
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* Who does what                                                       */
/* ------------------------------------------------------------------ */

/**
 * Service × person, as a grid of checkboxes over `item_resources`.
 *
 * **Not drawn until there is a choice to make.** One person and one room is a
 * grid with one column, which asks an owner to learn a widget to be told
 * something she already knows. Two of either and the question "who does the
 * facials" becomes real, and this is where she answers it.
 *
 * The write is `PATCH /v1/services/{id}` with the **whole** `resource_ids` list,
 * because that is what `setServiceResources` means: the request is the statement
 * of which resources deliver this service, and a link it dropped must actually
 * go away or the engine keeps offering a room the owner reassigned. So a tick
 * sends the new list, not a delta.
 *
 * ## A service that needs a person *and* a room
 *
 * The model cannot say it today, and this grid deliberately does not pretend it
 * can: a row of ticks means *any one of these delivers it*, and a massage ticked
 * for Maria and for Room 1 is a massage the engine will offer twice — once with
 * Maria and once with the room — and book against whichever lane the caller
 * picked. The design for the fix (resource **roles** per item: one of {people}
 * AND one of {rooms}) is written down in the spec under "The team, as built";
 * building half of it here would have been a second scheduling model.
 */
function MatrixPanel({
  people,
  rooms,
  services,
  owner,
}: {
  people: Resource[];
  rooms: Resource[];
  services: Service[];
  owner: boolean;
}): ReactNode {
  const actions = useActions();
  const t = useT();

  const lanes = [...people, ...rooms].filter((r) => r.active);
  if (lanes.length < 2) return null;

  const live = services.filter((s) => s.active);
  if (live.length === 0) return null;

  /**
   * **Roles are inferred from `resources.kind`, never ticked one by one**
   * (spec §4.1.2, migration 0056). The owner says one thing about a service —
   * *it needs one person and one room* — and this screen turns that into a
   * role per link: each ticked person becomes `'person'`, each ticked room
   * `'room'`, and equipment or a pool stays `'any'` (neither a person nor a
   * place the engine pairs by). Off puts every link back to `'any'`, which is
   * the union the engine has always walked.
   *
   * The write is one statement either way — the whole list, as
   * `setServiceResources` means it — in the spelling the server needs: the
   * plain `resource_ids` for an unroled service, so an older `core-api` and an
   * unchanged test read exactly what they always did, and `resources[]` with
   * the roles when the toggle is on.
   */
  const kindOf = new Map([...people, ...rooms].map((r) => [r.resource_id, r.kind] as const));
  const rolesFor = (
    ids: readonly string[],
  ): { resource_id: string; role: 'any' | 'person' | 'room' }[] =>
    ids.map((resource_id) => {
      const kind = kindOf.get(resource_id);
      return { resource_id, role: kind === 'person' ? 'person' : kind === 'room' ? 'room' : 'any' };
    });
  const isRoled = (service: Service): boolean => serviceRoleSides(service) !== null;
  const bothKinds = people.some((p) => p.active) && rooms.some((r) => r.active);

  const write = (service: Service, ids: readonly string[], roled: boolean): void => {
    void actions.updateService({
      serviceId: service.service_id,
      name: service.name,
      changes: roled ? { resources: rolesFor(ids) } : { resource_ids: [...ids] },
    });
  };

  const toggle = (service: Service, resourceId: string): void => {
    const next = service.resource_ids.includes(resourceId)
      ? service.resource_ids.filter((id) => id !== resourceId)
      : [...service.resource_ids, resourceId];
    write(service, next, isRoled(service));
  };

  const toggleNeedsBoth = (service: Service): void => {
    write(service, service.resource_ids, !isRoled(service));
  };

  return (
    <article className="panel">
      <h2>{t('team.whoDoesWhat')}</h2>
      <p className="muted">{t('team.whoDoesWhatHint')}</p>
      <table className="team-matrix">
        <thead>
          <tr>
            <th scope="col">{t('team.service')}</th>
            {lanes.map((lane) => (
              <th key={lane.resource_id} scope="col">
                {lane.name}
              </th>
            ))}
            {bothKinds ? <th scope="col">{t('team.needsBoth')}</th> : null}
          </tr>
        </thead>
        <tbody>
          {live.map((service) => (
            <tr key={service.service_id}>
              <th scope="row">{service.name}</th>
              {lanes.map((lane) => (
                <td key={lane.resource_id}>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      disabled={!owner}
                      checked={service.resource_ids.includes(lane.resource_id)}
                      onChange={() => toggle(service, lane.resource_id)}
                      aria-label={matrixLabel(t, service.name, lane.name)}
                    />
                    <span className="visually-hidden">
                      {matrixLabel(t, service.name, lane.name)}
                    </span>
                  </label>
                </td>
              ))}
              {bothKinds ? (
                <td>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      disabled={!owner}
                      checked={isRoled(service)}
                      onChange={() => toggleNeedsBoth(service)}
                      aria-label={t('team.needsBothCell', { service: service.name })}
                    />
                    <span className="visually-hidden">
                      {t('team.needsBothCell', { service: service.name })}
                    </span>
                  </label>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      {bothKinds ? <p className="muted">{t('team.needsBothHint')}</p> : null}
    </article>
  );
}

function matrixLabel(t: T, service: string, lane: string): string {
  return t('team.matrixCell', { service, who: lane });
}
